import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/app-shell';
import { useAuth } from './providers/auth';
import { AboutPage } from './routes/content-about';
import { AddonGroupFormPage, AddonsPage } from './routes/addons';
import { AddonOptionFormPage, AddonOptionsPage } from './routes/addon-options';
import { AuditPage } from './routes/audit';
import { BranchClosureFormPage } from './routes/branch-closure';
import { BranchDetailPage } from './routes/branch-detail';
import { BranchFormPage, BranchesPage } from './routes/branches';
import { CategoriesPage, CategoryFormPage } from './routes/categories';
import { ChangePasswordPage } from './routes/change-password';
import { DashboardPage } from './routes/dashboard';
import { FaqFormPage, FaqsPage } from './routes/faqs';
import { HomeContentPage } from './routes/content-home';
import { ItemFormPage, ItemsPage } from './routes/items';
import { LoginPage } from './routes/login';
import { MediaDetailPage, MediaPage } from './routes/media';
import { OfferFormPage, OffersPage } from './routes/offers';
import { QrPage } from './routes/qr';
import { SettingsPage } from './routes/settings';
import { UserFormPage, UsersPage } from './routes/users';

export function App() {
  const { user, initializing } = useAuth();
  const { t } = useTranslation();

  if (initializing) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <p className="dio-meta" role="status">
          {t('app.loading')}
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  /**
   * A generated password cannot outlive the first sign-in.
   *
   * The API enforces this in the guard, so skipping the screen by typing a URL
   * only produces 403s. Mirroring it here is what makes that a sensible flow
   * rather than a broken dashboard.
   */
  if (user.mustChangePassword) {
    return (
      <Routes>
        <Route path="*" element={<ChangePasswordPage forced />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="menu/categories" element={<CategoriesPage />} />
        <Route path="menu/categories/new" element={<CategoryFormPage />} />
        <Route path="menu/categories/:id" element={<CategoryFormPage />} />
        <Route path="menu/items" element={<ItemsPage />} />
        <Route path="menu/items/new" element={<ItemFormPage />} />
        <Route path="menu/items/:id" element={<ItemFormPage />} />
        <Route path="menu/addons" element={<AddonsPage />} />
        <Route path="menu/addons/new" element={<AddonGroupFormPage />} />
        <Route path="menu/addons/:id" element={<AddonGroupFormPage />} />
        <Route path="menu/addons/:groupId/options" element={<AddonOptionsPage />} />
        <Route path="menu/addons/:groupId/options/new" element={<AddonOptionFormPage />} />
        <Route path="menu/addons/:groupId/options/:optionId" element={<AddonOptionFormPage />} />
        <Route path="offers" element={<OffersPage />} />
        <Route path="offers/new" element={<OfferFormPage />} />
        <Route path="offers/:id" element={<OfferFormPage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="branches/new" element={<BranchFormPage />} />
        <Route path="branches/:id" element={<BranchDetailPage />} />
        <Route path="branches/:id/edit" element={<BranchFormPage />} />
        <Route path="branches/:id/closures/new" element={<BranchClosureFormPage />} />
        <Route path="branches/:id/closures/:closureId" element={<BranchClosureFormPage />} />
        <Route path="content/home" element={<HomeContentPage />} />
        <Route path="content/about" element={<AboutPage />} />
        <Route path="content/faqs" element={<FaqsPage />} />
        <Route path="content/faqs/new" element={<FaqFormPage />} />
        <Route path="content/faqs/:id" element={<FaqFormPage />} />
        <Route path="media" element={<MediaPage />} />
        <Route path="media/:id" element={<MediaDetailPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/new" element={<UserFormPage />} />
        <Route path="users/:id" element={<UserFormPage />} />
        <Route path="qr" element={<QrPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="account/password" element={<ChangePasswordPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
